/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  Share,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { KeyboardAvoidingView} from 'react-native-keyboard-controller';
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { StatusPill } from '../../shared/surface-cards/StatusPill'
import type { StatusPillTone } from '../../shared/surface-cards/StatusPill';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { purchaseCreditBannerText, purchaseOrderStatusLabel } from '../domain/purchaseLabels';
import {
  canClosePurchaseOrder,
  canReceivePurchaseOrder,
  canSendPurchaseOrder,
  canVoidPurchaseOrder,
  validateCloseReason,
  validateReceptionForm,
  validateVoidReason,
  splitEvenly,
  validateVariantDistribution,
  type ReceptionVariantSlice,
} from '../domain/purchaseValidation';
import { buildPurchaseOrderShareText, canSharePurchaseOrder } from '../domain/purchaseOrderShareText';
import { getFiscalProfile } from '../../fiscal/infra-api';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { pickAttachmentFile } from '../../shared/attachments/infra/pickAttachmentFile'
import { saveExpenseAttachmentLocal, listAttachmentsForExpense } from '../../expenses/infra/expenseAttachmentsRepo'
import { expenseErpCopy } from '../../expenses/domain/expenseErpCopy'
import type { ExpenseRow, ExpenseAttachmentRow } from '../../expenses/domain/expenseTypes';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { getErrorMessage } from '../../shared/errorMessage';
import { isUnexpectedFailureHandled, presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { listVariantsForProduct } from '../../catalog/infra-api'
import type { ProductVariant } from '../../catalog/domain-api';
import { useProductVariantsEnabled } from '../../catalog/app-api';
import type { PurchaseOrderWithItems, PurchaseOrderStatus, PurchaseReceptionRow, PurchaseReturnRow } from '../domain/purchaseTypes';
import type { CreateReceptionItemInput } from '../infra/purchasesRepoMutations';

import { formatMoney } from '../../shared/format/public-api';
import { ExpenseFiscalSection } from '../../expenses/components/ExpenseFiscalSection';
import {
  emptyExpenseFiscalDraft,
  expenseFiscalDraftIsEmpty,
  expenseFiscalDraftToInput,
  validateExpenseFiscalInvoice,
  type ExpenseFiscalDraft,
} from '../../expenses/domain-api';
import type { ExpenseSettleMethod } from '../../expenses/components/expenseSettlementMethods';
import {
  canSubmitExpensePayment,
  resolveSettleMethodFromPaymentForm,
  type ActiveCashSessionInfo,
  type ExpenseAlreadyPaidKind,
  type ExpensePaidFormChoice,
} from '../../expenses/components/expensePaymentFlow';
import { todayIsoDate } from '../../expenses/infra/expenseListSql';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import type { PurchasePaymentOnReceive } from '../application/purchaseReceptionPayment';
import { buildPurchasePaymentOnReceive } from '../application/purchaseReceptionPayment';
import {
  defaultReceptionPaidForm,
  PurchaseReceptionPaymentBlock,
} from './PurchaseReceptionPaymentBlock';
type Props = {
  visible: boolean;
  order: PurchaseOrderWithItems | null;
  onClose: () => void;
  onVoid: (orderId: string, voidReason: string) => void;
  onSend: (orderId: string) => void;
  onReceive: (payload: {
    order: PurchaseOrderWithItems;
    items: CreateReceptionItemInput[];
    generateExpense: boolean;
    alreadyPaid: boolean;
    paymentOnReceive?: PurchasePaymentOnReceive;
    fiscal?: import('../../expenses/domain/expenseTypes').ExpenseFiscalInvoiceInput | null;
  }) => void;
  activeCashSession?: ActiveCashSessionInfo | null;
  expectedCashInDrawer?: number | null;
  onOpenCash?: () => void;
  onCompleteExpenseFiscal?: (expense: ExpenseRow) => void;
  fiscalCaptureEnabled?: boolean;
  onRegisterSupplierCreditNote?: (params: {
    returnId: string;
    receptionId: string | null;
    order: PurchaseOrderWithItems;
  }) => void;
  onReturn: (order: PurchaseOrderWithItems) => void;
  onPay: (expense: ExpenseRow) => void;
  /** OC anulada → reabre el registro como borrador con los mismos items. */
  onDuplicate?: (order: PurchaseOrderWithItems) => void;
  onCloseLeftover: (orderId: string, closeReason: string) => void;
  // BUG-45: Handlers para anular recepciones y devoluciones
  onVoidReception: (receptionId: string, voidReason: string) => void;
  onVoidReturn: (returnId: string, voidReason: string) => void;
  linkedExpense: ExpenseRow | null;
  /** Todos los gastos activos ligados a la OC (puede haber uno por recepción). */
  linkedExpenses?: ExpenseRow[];
  // BUG-45: Listas de recepciones y devoluciones ligadas a la orden
  receptions: PurchaseReceptionRow[];
  returns: PurchaseReturnRow[];
  canEdit: boolean;
  canReceive: boolean;
  canVoid: boolean;
  canReturn: boolean;
  canPay: boolean;
  hasReturn: boolean;
  submitting: boolean;
  createdBy?: string | null;
};

function statusTone(status: PurchaseOrderStatus): StatusPillTone {
  switch (status) {
    case 'received':
      return 'success';
    case 'partial':
      return 'warning';
    case 'cancelled':
      return 'danger';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}


type ReceptionFormItem = {
  purchaseOrderItemId: string;
  productId: string | null;
  productName: string | null;
  pending: number;
  quantity: string;
  unitCost: string;
  targetBranchId: string | null;
  /** #694: variantes activas del producto del ítem (vacío = sin variantes). */
  variants: ProductVariant[];
  /** #694: distribución por variante; null = sin distribuir (recepción al parent). */
  distribution: Record<string, string> | null;
  expanded: boolean;
};

/** Label corto de variante (mismo criterio que PosVariantPickerModal). */
function variantLabel(variant: ProductVariant): string {
  const suffix = variant.nameSuffix?.trim();
  if (suffix) return suffix;
  const attrs = Object.entries(variant.attributes ?? {});
  if (attrs.length > 0) return attrs.map(([, v]) => v).join(' · ');
  return 'Variante';
}

function distributionSum(distribution: Record<string, string> | null): number {
  if (!distribution) return 0;
  return Object.values(distribution).reduce((sum, raw) => sum + (parseDecimalInput(raw) ?? 0), 0);
}

export function PurchaseOrderDetailModal({
  visible,
  order,
  onClose,
  onVoid,
  onSend,
  onReceive,
  onReturn,
  onPay,
  onDuplicate,
  onCloseLeftover,
  onVoidReception,
  onVoidReturn,
  linkedExpense,
  linkedExpenses,
  receptions,
  returns,
  canEdit,
  canReceive,
  canVoid,
  canReturn,
  canPay,
  hasReturn,
  submitting,
  createdBy,
  onCompleteExpenseFiscal,
  fiscalCaptureEnabled = false,
  onRegisterSupplierCreditNote,
  activeCashSession = null,
  expectedCashInDrawer = null,
  onOpenCash,
}: Props) {
  const { width, height: winH } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const backdropPadding = useModalBackdropPadding();

  const [receptionMode, setReceptionMode] = useState(false);
  const [receptionItems, setReceptionItems] = useState<ReceptionFormItem[]>([]);
  // #694: solo consultamos variantes si la org tiene el módulo habilitado
  const variantsEnabled = useProductVariantsEnabled();
  const [generateExpense, setGenerateExpense] = useState(true);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [paidForm, setPaidForm] = useState<ExpensePaidFormChoice | null>(null);
  const [alreadyPaidKind, setAlreadyPaidKind] = useState<ExpenseAlreadyPaidKind | null>(null);
  const [nonDrawerMethod, setNonDrawerMethod] = useState<ExpenseSettleMethod>('transfer');
  const [settleNote, setSettleNote] = useState('');
  const [paidDate, setPaidDate] = useState(todayIsoDate());
  const [pastCashDrawerConfirmed, setPastCashDrawerConfirmed] = useState(false);
  const [receptionFiscalOpen, setReceptionFiscalOpen] = useState(false);
  const [receptionFiscalDraft, setReceptionFiscalDraft] = useState<ExpenseFiscalDraft>(
    emptyExpenseFiscalDraft(),
  );
  const [voidReason, setVoidReason] = useState('');
  const [voidPrompt, setVoidPrompt] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closePrompt, setClosePrompt] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [costHint, setCostHint] = useState('Costo unit.');
  const [attachments, setAttachments] = useState<ExpenseAttachmentRow[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  // BUG-45: Estado para anular recepciones y devoluciones
  const [voidReceptionTarget, setVoidReceptionTarget] = useState<string | null>(null);
  const [voidReceptionReason, setVoidReceptionReason] = useState('');
  const [voidReturnTarget, setVoidReturnTarget] = useState<string | null>(null);
  const [voidReturnReason, setVoidReturnReason] = useState('');
  const { confirm, ConfirmModal: ReceptionConfirmModal } = useOperationalConfirm();
  const activeCashSessionId = activeCashSession?.session.id ?? null;
  const receptionExpenseDate = todayIsoDate();

  useEffect(() => {
    if (visible && order) {
      setReceptionMode(false);
      setGenerateExpense(true);
      setAlreadyPaid(false);
      setPaidForm(null);
      setAlreadyPaidKind(null);
      setNonDrawerMethod('transfer');
      setSettleNote('');
      setPaidDate(todayIsoDate());
      setPastCashDrawerConfirmed(false);
      setReceptionFiscalOpen(false);
      setReceptionFiscalDraft(emptyExpenseFiscalDraft());
      setVoidReason('');
      setVoidPrompt(false);
      setCloseReason('');
      setClosePrompt(false);
      setActionError(null);
      setAttachments([]);
      setAttachmentBusy(false);
      void getFiscalProfile(order.branchId)
        .then((profile) => {
          setCostHint(
            profile?.taxRegime === 'responsable_inscripto' ? 'Costo neto' : 'Costo unit.',
          );
        })
        .catch(() => setCostHint('Costo unit.'));
      setReceptionItems(
        order.items.map((item) => ({
          purchaseOrderItemId: item.id,
          productId: item.productId,
          productName: item.productName,
          pending: Math.max(0, item.quantity - item.receivedQuantity),
          quantity: String(Math.max(0, item.quantity - item.receivedQuantity)),
          unitCost: item.unitCost ? String(item.unitCost) : '',
          targetBranchId: item.targetBranchId ?? null,
          // #694: variantes se cargan async (vacío mientras tanto → flujo sin variantes)
          variants: [],
          distribution: null,
          expanded: false,
        })),
      );
      // #694: cargar variantes activas por ítem para saber dónde pedir distribución
      if (!variantsEnabled) return;
      const productIds = Array.from(
        new Set(order.items.map((item) => item.productId).filter((id): id is string => Boolean(id))),
      );
      void Promise.all(
        productIds.map(async (productId) => ({ productId, variants: await listVariantsForProduct(productId) })),
      )
        .then((results) => {
          const byProduct = new Map(results.map((r) => [r.productId, r.variants]));
          setReceptionItems((prev) =>
            prev.map((item) => {
              const variants = item.productId ? byProduct.get(item.productId) ?? [] : [];
              return { ...item, variants };
            }),
          );
        })
        .catch(() => {
          // sin variantes cargadas → recepción al parent (compatibilidad)
        });
    }
  }, [visible, order, variantsEnabled]);

  useEffect(() => {
    if (!visible || !linkedExpense) {
      setAttachments([]);
      return;
    }
    let cancelled = false;
    void listAttachmentsForExpense(linkedExpense.id)
      .then((rows) => {
        if (!cancelled) setAttachments(rows);
      })
      .catch(() => {
        if (!cancelled) setAttachments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, linkedExpense?.id]);

  const settleMethod = useMemo(
    () =>
      paidForm
        ? resolveSettleMethodFromPaymentForm({ paidForm, alreadyPaidKind, nonDrawerMethod })
        : 'transfer',
    [paidForm, alreadyPaidKind, nonDrawerMethod],
  );

  if (!order) return null;

  const canVoidOrder = canVoid && canVoidPurchaseOrder(order.status);
  const canSendOrder = canEdit && canSendPurchaseOrder(order.status);
  const canReceiveOrder =
    canReceive &&
    canReceivePurchaseOrder(order.status) &&
    order.items.some((i) => i.receivedQuantity < i.quantity);
  const canReturnOrder =
    canReturn && !hasReturn && order.status !== 'cancelled' && order.items.some((i) => i.receivedQuantity > 0);
  const canPayOrder =
    canPay &&
    linkedExpense != null &&
    (linkedExpense.paymentStatus === 'unpaid' || linkedExpense.paymentStatus === 'partial');
  const canCloseOrder = canEdit && canClosePurchaseOrder(order.status, order.items);
  const canShareOrder = canSharePurchaseOrder(order.status);
  // BUG-31: Solo mostrar el banner si hay crédito real (overpay > 0)
  const showCreditBanner =
    hasReturn && linkedExpense != null && linkedExpense.paymentStatus === 'paid';
  const creditBannerText = showCreditBanner && linkedExpense
    ? purchaseCreditBannerText(Math.max(0, linkedExpense.amountPaid - linkedExpense.amount))
    : null;

  const handleReceptionItemChange = (index: number, field: keyof ReceptionFormItem, value: string) => {
    setReceptionItems(receptionItems.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  // #694: helpers de distribución por variante
  const handleDistributionChange = (index: number, variantId: string, value: string) => {
    setReceptionItems(
      receptionItems.map((item, i) =>
        i === index && item.distribution
          ? { ...item, distribution: { ...item.distribution, [variantId]: value } }
          : item,
      ),
    );
  };

  const handleToggleDistribution = (index: number) => {
    setReceptionItems(
      receptionItems.map((item, i) => {
        if (i !== index) return item;
        if (item.expanded) return { ...item, expanded: false };
        const distribution: Record<string, string> = {};
        for (const variant of item.variants) distribution[variant.id] = '';
        return { ...item, expanded: true, distribution };
      }),
    );
  };

  const handleDistributeEvenly = (index: number) => {
    setReceptionItems(
      receptionItems.map((item, i) => {
        if (i !== index) return item;
        const qty = parseDecimalInput(item.quantity) ?? 0;
        const parts = splitEvenly(qty, item.variants.length);
        const distribution: Record<string, string> = {};
        item.variants.forEach((variant, vIndex) => {
          distribution[variant.id] = String(parts[vIndex] ?? 0);
        });
        return { ...item, distribution };
      }),
    );
  };

  const handleConfirmReception = async () => {
    const receptionFormItems = receptionItems.map((item) => {
      const qty = parseDecimalInput(item.quantity) ?? 0;
      const distributionSlices: ReceptionVariantSlice[] | null = item.distribution
        ? Object.entries(item.distribution)
            .map(([variantId, raw]) => ({ variantId, quantity: parseDecimalInput(raw) ?? 0 }))
            .filter((slice) => slice.quantity !== 0)
        : null;
      return { item, qty, distributionSlices };
    });

    // #694: validar distribución por variante antes de armar los items
    for (const { item, qty, distributionSlices } of receptionFormItems) {
      if (item.variants.length > 0 && qty > 0) {
        const check = validateVariantDistribution({
          quantity: qty,
          variants: item.variants,
          distribution: distributionSlices ?? [],
        });
        if (!check.ok) {
          setActionError(check.message);
          return;
        }
      }
    }

    const itemsToReceive: CreateReceptionItemInput[] = [];
    for (const { item, qty, distributionSlices } of receptionFormItems) {
      if (qty <= 0) continue;
      const base = {
        purchaseOrderItemId: item.purchaseOrderItemId,
        productId: item.productId ?? '',
        productName: item.productName,
        productSku: null,
        unitCost: item.unitCost ? parseDecimalInput(item.unitCost) : null,
        unitOfMeasure: null,
        targetBranchId: item.targetBranchId ?? null,
      };
      const hasDistribution = distributionSlices != null && distributionSlices.length > 0;
      if (hasDistribution && item.variants.length > 0) {
        // #694: una porción por variante (solo > 0); el costo viaja en cada porción
        for (const slice of distributionSlices!) {
          itemsToReceive.push({
            ...base,
            itemId: crypto.randomUUID(),
            quantity: slice.quantity,
            variantId: slice.variantId,
          });
        }
      } else {
        // Sin variantes, sin expandir, o distribución vacía: flujo de siempre, recepción al parent
        itemsToReceive.push({ ...base, quantity: qty, variantId: null });
      }
    }

    // BUG-23/26: Pasar pending y unitCost a la validación (una entrada por ítem de la OC)
    const validation = validateReceptionForm(
      receptionFormItems
        .filter(({ qty }) => qty > 0)
        .map(({ item, qty }) => ({
          quantity: qty,
          pending: item.pending,
          unitCost: item.unitCost ? parseDecimalInput(item.unitCost) : null,
        })),
    );
    if (!validation.ok) {
      setActionError(validation.message);
      return;
    }
    let fiscalPayload: ReturnType<typeof expenseFiscalDraftToInput> = null;
    if (fiscalCaptureEnabled && !expenseFiscalDraftIsEmpty(receptionFiscalDraft)) {
      fiscalPayload = expenseFiscalDraftToInput(receptionFiscalDraft);
      if (!fiscalPayload) {
        setActionError('Completá el comprobante fiscal o dejalo en “Sin comprobante”.');
        return;
      }
      const fiscalError = validateExpenseFiscalInvoice(fiscalPayload);
      if (fiscalError) {
        setActionError(fiscalError);
        return;
      }
    }

    const willGenerateExpense = generateExpense || fiscalPayload != null;
    const payOnReceive = willGenerateExpense && alreadyPaid;
    let paymentOnReceive: PurchasePaymentOnReceive | undefined;
    if (payOnReceive) {
      if (
        !canSubmitExpensePayment({
          paymentStatus: 'paid',
          paidForm,
          alreadyPaidKind,
          settleMethod,
          activeCashSessionId,
          settleNote,
          pastCashDrawerConfirmed,
          expenseDate: receptionExpenseDate,
          amount: receptionTotal,
        })
      ) {
        setActionError('Completá cómo se pagó.');
        return;
      }
      if (
        settleMethod === 'cash_drawer' &&
        expectedCashInDrawer != null &&
        receptionTotal > expectedCashInDrawer
      ) {
        const ok = await confirm({
          title: 'Sale más plata de la que hay en caja',
          message: `Querés sacar ${formatMoney(receptionTotal)} y en caja hay ${formatMoney(expectedCashInDrawer)}. ¿Lo registrás igual?`,
          confirmLabel: 'Registrar',
        });
        if (!ok) return;
      }
      try {
        paymentOnReceive = buildPurchasePaymentOnReceive({
          paidForm,
          alreadyPaidKind,
          nonDrawerMethod,
          settleNote,
          paidDate,
          activeCashSessionId,
        });
      } catch (err) {
        setActionError(getErrorMessage(err));
        return;
      }
    }

    setActionError(null);
    onReceive({
      order,
      items: itemsToReceive,
      generateExpense: willGenerateExpense,
      alreadyPaid: payOnReceive,
      paymentOnReceive,
      fiscal: fiscalPayload,
    });
  };

  const handleVoidPress = () => {
    // BUG-44: Resetear closePrompt al activar voidPrompt
    setClosePrompt(false);
    if (!voidPrompt) {
      setVoidPrompt(true);
      return;
    }
    const validation = validateVoidReason(voidReason);
    if (!validation.ok) {
      setActionError(validation.message);
      return;
    }
    setActionError(null);
    onVoid(order.id, voidReason.trim());
  };

  const handleCloseLeftoverPress = () => {
    // BUG-44: Resetear voidPrompt al activar closePrompt
    setVoidPrompt(false);
    if (!closePrompt) {
      setClosePrompt(true);
      return;
    }
    const validation = validateCloseReason(closeReason);
    if (!validation.ok) {
      setActionError(validation.message);
      return;
    }
    setActionError(null);
    onCloseLeftover(order.id, closeReason.trim());
  };

  const handleShare = async () => {
    try {
      await Share.share({ message: buildPurchaseOrderShareText(order) });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.toLowerCase().includes('cancel')) return;
      setActionError('No pude compartir el pedido.');
    }
  };

  const handleAttachReceipt = async () => {
    if (!linkedExpense) return;
    try {
      const picked = await pickAttachmentFile('purchases');
      if (!picked) return;
      const orgId = await getSessionOrganizationId();
      if (!orgId) {
        showError('Error', 'No pude ubicar la organización.');
        return;
      }
      setAttachmentBusy(true);
      await saveExpenseAttachmentLocal({
        attachmentId: crypto.randomUUID(),
        expenseId: linkedExpense.id,
        branchId: order.branchId,
        orgId,
        fileName: picked.fileName,
        mimeType: picked.mimeType,
        fileSize: picked.fileSize,
        fileBase64: picked.base64,
        createdBy: createdBy ?? null,
      });
      const rows = await listAttachmentsForExpense(linkedExpense.id);
      setAttachments(rows);
      showSuccess('Comprobante adjuntado');
    } catch (error: unknown) {
      if (isUnexpectedFailureHandled(error)) return;
      presentUnexpectedFailure({ module: 'purchases', op: 'attach_ticket', error, userMessage: 'No pude adjuntar el ticket.' });
    } finally {
      setAttachmentBusy(false);
    }
  };

  const totalReceived = order.items.reduce((sum, i) => sum + i.receivedQuantity, 0);
  const totalOrdered = order.items.reduce((sum, i) => sum + i.quantity, 0);
  const progressRatio = totalOrdered > 0 ? Math.min(1, totalReceived / totalOrdered) : 0;
  // Total a recibir ahora: Σ cantidad × costo de la recepción en curso (proto compras v-receive).
  const receptionTotal = receptionItems.reduce((sum, item) => {
    const qty = parseDecimalInput(item.quantity) ?? 0;
    const cost = parseDecimalInput(item.unitCost) ?? 0;
    return sum + qty * cost;
  }, 0);
  const confirmReceptionLabel =
    receptionTotal > 0
      ? `Confirmar recepción · ${formatMoney(receptionTotal)}`
      : 'Confirmar recepción';
  const orderTitle = order.orderNumber ?? 'Orden de compra';
  // Todos los gastos ligados (fallback al singular para callers viejos).
  const allLinkedExpenses =
    linkedExpenses && linkedExpenses.length > 0
      ? linkedExpenses
      : linkedExpense
        ? [linkedExpense]
        : [];

  // Proto compras v-detail — derivados del header y el stat-row.
  const shortDay = (iso: string | null | undefined) =>
    iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : null;
  const todayIso = new Date().toLocaleDateString('en-CA');
  const headerSub = [
    shortDay(order.createdAt),
    order.expectedDelivery ? `entrega ${shortDay(order.expectedDelivery)}` : null,
    order.supplierInvoiceRef ? `factura ${order.supplierInvoiceRef}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const expensePendingTotal = allLinkedExpenses.reduce(
    (sum, e) => sum + Math.max(0, e.amount - e.amountPaid),
    0,
  );
  const overdueExpense =
    allLinkedExpenses.find(
      (e) => e.paymentStatus !== 'paid' && e.dueDate != null && e.dueDate < todayIso,
    ) ?? null;
  const overdueDays = overdueExpense?.dueDate
    ? Math.max(
        1,
        Math.floor((Date.parse(todayIso) - Date.parse(overdueExpense.dueDate)) / 86400000),
      )
    : 0;
  const itemsFullyReceived = order.items.filter(
    (i) => i.receivedQuantity >= i.quantity,
  ).length;
  const payStat = overdueExpense
    ? { text: `venció ${shortDay(overdueExpense.dueDate)}`, tone: 'bad' as const }
    : expensePendingTotal > 0
      ? { text: 'pendiente', tone: 'warn' as const }
      : allLinkedExpenses.length > 0
        ? { text: 'pagado', tone: 'ok' as const }
        : { text: '—', tone: 'muted' as const };
  const canDuplicate = order.status === 'cancelled' && canEdit && onDuplicate != null;

  const content = receptionMode ? (
    <>
      {receptionItems.map((item, index) => {
        const hasVariants = item.variants.length > 0;
        const distSum = distributionSum(item.distribution);
        const receivedQty = parseDecimalInput(item.quantity) ?? 0;
        const distMismatch = hasVariants && item.expanded && Math.abs(distSum - receivedQty) > 1e-6;
        return (
        <View key={index} style={styles.receptionItemRow}>
          <Text style={styles.receptionItemName}>{item.productName ?? 'Producto'}</Text>
          <Text style={styles.receptionItemPending}>Pendiente: {item.pending}</Text>
          <View style={styles.receptionItemInputs}>
            <View style={styles.flex1}>
              <Text style={styles.inputLabel}>Recibido</Text>
              <NumericTextInput
                kind="decimalMoney"
                value={item.quantity}
                onChangeText={(v) => handleReceptionItemChange(index, 'quantity', v)}
                placeholder="0"
              />
            </View>
            <View style={styles.flex1}>
              <Text style={styles.inputLabel}>{costHint}</Text>
              <NumericTextInput
                kind="decimalMoney"
                value={item.unitCost}
                onChangeText={(v) => handleReceptionItemChange(index, 'unitCost', v)}
                placeholder="0"
              />
            </View>
          </View>
          {hasVariants ? (
            <View style={styles.variantSection}>
              <TouchableOpacity
                style={styles.variantToggleBtn}
                onPress={() => handleToggleDistribution(index)}
                accessibilityRole="button"
                accessibilityLabel={
                  item.expanded ? 'Ocultar distribución por variante' : 'Distribuir por variante'
                }
              >
                <Text style={styles.variantToggleText}>
                  {item.expanded ? '▾' : '▸'} ¿Cómo distribuí lo recibido?
                </Text>
              </TouchableOpacity>
              {item.expanded ? (
                <View style={styles.variantSectionBody}>
                  <TouchableOpacity
                    style={styles.variantEvenBtn}
                    onPress={() => handleDistributeEvenly(index)}
                    accessibilityRole="button"
                    accessibilityLabel="Distribuir parejo"
                  >
                    <Text style={styles.variantEvenBtnText}>Distribuir parejo</Text>
                  </TouchableOpacity>
                  {item.variants.map((variant) => (
                    <View key={variant.id} style={styles.variantRow}>
                      <Text style={styles.variantLabel}>{variantLabel(variant)}</Text>
                      <View style={styles.variantInputWrap}>
                        <NumericTextInput
                          kind="decimalMoney"
                          value={item.distribution?.[variant.id] ?? ''}
                          onChangeText={(v) => handleDistributionChange(index, variant.id, v)}
                          placeholder="0"
                        />
                      </View>
                    </View>
                  ))}
                  <Text
                    style={[
                      styles.variantSumText,
                      distMismatch ? styles.variantSumMismatch : styles.variantSumOk,
                    ]}
                  >
                    Distribuido: {distSum} de {receivedQty}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
        );
      })}
      <View style={styles.expenseToggleRow}>
        <TouchableOpacity
          style={[styles.checkbox, generateExpense && styles.checkboxActive]}
          onPress={() => {
            setGenerateExpense(!generateExpense);
            if (generateExpense) setAlreadyPaid(false);
          }}
        >
          {generateExpense ? <Text style={styles.checkboxTick}>✓</Text> : null}
        </TouchableOpacity>
        <Text style={styles.expenseToggleLabel}>Generar gasto de proveedor</Text>
      </View>
      {generateExpense ? (
        <>
          <View style={styles.expenseToggleRow}>
            <TouchableOpacity
              style={[styles.checkbox, alreadyPaid && styles.checkboxActive]}
              onPress={() => {
                const next = !alreadyPaid;
                setAlreadyPaid(next);
                if (next) {
                  setPaidForm(defaultReceptionPaidForm(activeCashSessionId));
                }
              }}
              accessibilityRole="checkbox"
              accessibilityLabel="Ya pagué"
              accessibilityHint="Si pagás con efectivo de la caja, elegí ese medio abajo para que descuente el turno."
            >
              {alreadyPaid ? <Text style={styles.checkboxTick}>✓</Text> : null}
            </TouchableOpacity>
            <Text style={styles.expenseToggleLabel}>Ya pagué</Text>
          </View>
          {alreadyPaid ? (
            <PurchaseReceptionPaymentBlock
              expenseDate={receptionExpenseDate}
              paidForm={paidForm}
              onPaidFormChange={setPaidForm}
              alreadyPaidKind={alreadyPaidKind}
              onAlreadyPaidKindChange={setAlreadyPaidKind}
              nonDrawerMethod={nonDrawerMethod}
              onNonDrawerMethodChange={setNonDrawerMethod}
              settleNote={settleNote}
              onSettleNoteChange={setSettleNote}
              paidDate={paidDate}
              onPaidDateChange={setPaidDate}
              activeCashSession={activeCashSession}
              pastCashDrawerConfirmed={pastCashDrawerConfirmed}
              onPastCashDrawerConfirmedChange={setPastCashDrawerConfirmed}
              submitting={submitting}
              onOpenCash={onOpenCash}
              testIDPrefix="purchases.receive.payment"
            />
          ) : null}
        </>
      ) : null}
      {fiscalCaptureEnabled ? (
        <View style={styles.fiscalBlock}>
          <TouchableOpacity
            accessibilityRole="button"
            testID="purchases.receive.fiscal.toggle"
            onPress={() => setReceptionFiscalOpen((open) => !open)}
            disabled={submitting}
          >
            <Text style={styles.fiscalToggle}>
              {receptionFiscalOpen ? '▾' : '▸'} ¿Tenés factura del proveedor?
            </Text>
          </TouchableOpacity>
          {receptionFiscalOpen ? (
            <ExpenseFiscalSection
              draft={receptionFiscalDraft}
              onChange={(draft) => {
                setReceptionFiscalDraft(draft);
                if (!expenseFiscalDraftIsEmpty(draft)) setGenerateExpense(true);
              }}
              expenseAmount={receptionTotal > 0 ? receptionTotal : null}
              submitting={submitting}
              branchId={order.branchId}
              testIDPrefix="purchases.receive.fiscal"
            />
          ) : (
            <Text style={styles.fiscalHint}>Sin factura por ahora — podés cargarla después en el detalle del pedido.</Text>
          )}
        </View>
      ) : null}
    </>
  ) : (
    <View style={styles.detailBody}>
      {/* Proto v-detail: stat-row — total / items / gasto pendiente / pago */}
      <View style={styles.statRow}>
        <View style={[styles.statCell, styles.statDivider]}>
          <Text style={styles.statValue}>{formatMoney(order.total)}</Text>
          <Text style={styles.statLabel}>total orden</Text>
        </View>
        <View style={[styles.statCell, styles.statDivider]}>
          <Text style={styles.statValue}>
            {itemsFullyReceived}/{order.items.length}
          </Text>
          <Text style={styles.statLabel}>items recibidos</Text>
        </View>
        <View style={[styles.statCell, styles.statDivider]}>
          <Text style={[styles.statValue, expensePendingTotal > 0 && styles.statValueWarn]}>
            {expensePendingTotal > 0 ? formatMoney(expensePendingTotal) : '—'}
          </Text>
          <Text style={styles.statLabel}>gasto pendiente</Text>
        </View>
        <View style={styles.statCell}>
          <Text
            style={[
              styles.statValue,
              payStat.tone === 'bad' && styles.statValueBad,
              payStat.tone === 'warn' && styles.statValueWarn,
              payStat.tone === 'ok' && styles.statValueOk,
              payStat.tone === 'muted' && styles.statValueMuted,
            ]}
          >
            {payStat.text}
          </Text>
          <Text style={styles.statLabel}>pago</Text>
        </View>
      </View>

      {/* Proto v-detail: barra de progreso con label + % */}
      <View
        style={styles.progressBlock}
        accessibilityLabel={`Recibido ${totalReceived} de ${totalOrdered}`}
      >
        <View style={styles.progressLabelRow}>
          <Text style={styles.progressLabel}>Progreso de recepción</Text>
          <Text style={styles.progressPct}>{Math.round(progressRatio * 100)}%</Text>
        </View>
        <View style={styles.progressTrack}>
          <View
            style={[styles.progressFill, { width: `${Math.round(progressRatio * 100)}%` }]}
          />
        </View>
      </View>

      {/* Proto v-detail: warn-box de gasto vencido con acceso directo al pago */}
      {overdueExpense ? (
        <TouchableOpacity
          style={styles.warnBox}
          disabled={!canPay || submitting}
          onPress={() => onPay(overdueExpense)}
          accessibilityRole={canPay ? 'button' : 'text'}
        >
          <Text style={styles.warnBoxText}>
            El gasto venció hace {overdueDays} {overdueDays === 1 ? 'día' : 'días'}.
            {canPay ? ' Pagar ahora ›' : ''}
          </Text>
        </TouchableOpacity>
      ) : null}

      {order.status === 'cancelled' && (order.voidReason || order.voidedAt) ? (
        <View style={styles.voidInfoBox} accessibilityRole="text">
          <Text style={styles.voidInfoTitle}>Pedido anulado</Text>
          {order.voidReason ? (
            <Text style={styles.voidInfoText}>Motivo: {order.voidReason}</Text>
          ) : null}
          {order.voidedAt ? (
            <Text style={styles.voidInfoText}>
              El {shortDay(order.voidedAt)}
              {order.voidedBy ? ` por ${order.voidedBy}` : ''}
            </Text>
          ) : null}
        </View>
      ) : null}

      {order.status === 'closed' && (order.closeReason || order.closedAt) ? (
        <View style={styles.closeInfoBox} accessibilityRole="text">
          <Text style={styles.voidInfoTitle}>Pedido cerrado</Text>
          {order.closeReason ? (
            <Text style={styles.voidInfoText}>Motivo: {order.closeReason}</Text>
          ) : null}
          {order.closedAt ? (
            <Text style={styles.voidInfoText}>
              El {shortDay(order.closedAt)}
              {order.closedBy ? ` por ${order.closedBy}` : ''}
            </Text>
          ) : null}
        </View>
      ) : null}

      {creditBannerText ? (
        <View style={styles.creditBanner} accessibilityRole="text">
          <Text style={styles.creditBannerText}>{creditBannerText}</Text>
        </View>
      ) : null}

      {/* Proto v-detail: items como mini-rows con cantidad + pill de estado */}
      <View style={styles.secDivided}>
        <Text style={styles.secTitle}>Items ({order.items.length})</Text>
        {order.items.map((item, index) => {
          const fully = item.receivedQuantity >= item.quantity;
          const partial = !fully && item.receivedQuantity > 0;
          return (
            <View
              key={item.id}
              style={[styles.miniRow, index === order.items.length - 1 && styles.miniRowLast]}
            >
              <View style={styles.miniMain}>
                <Text style={styles.miniTitle} numberOfLines={1}>
                  {item.productName ?? 'Producto'}
                </Text>
                <Text style={styles.miniMeta} numberOfLines={1}>
                  {[
                    item.productSku,
                    item.unitCost ? `costo ${formatMoney(item.unitCost)}` : 'sin costo',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </View>
              <Text style={styles.miniValue}>
                {item.receivedQuantity}/{item.quantity}
              </Text>
              <StatusPill
                label={fully ? 'Recibido' : partial ? 'Parcial' : 'Pendiente'}
                tone={fully ? 'success' : 'warning'}
                compact
              />
            </View>
          );
        })}
      </View>

      {/* BUG-45 + proto: recepciones como mini-rows con pill y anulación en 2 pasos */}
      {receptions.length > 0 ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>Recepciones ({receptions.length})</Text>
          {receptions.map((rec, index) => (
            <View
              key={rec.id}
              style={[styles.miniRow, index === receptions.length - 1 && styles.miniRowLast]}
            >
              <View style={styles.miniMain}>
                <Text style={styles.miniTitle}>Recepción {shortDay(rec.receivedAt)}</Text>
                <Text style={styles.miniMeta} numberOfLines={1}>
                  {[
                    rec.supplierInvoiceRef ? `factura ${rec.supplierInvoiceRef}` : null,
                    rec.receivedBy ? `por ${rec.receivedBy}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'sin factura'}
                </Text>
              </View>
              <StatusPill
                label={rec.status === 'active' ? 'Activa' : 'Anulada'}
                tone={rec.status === 'active' ? 'success' : 'neutral'}
                compact
              />
              {rec.status === 'active' && canVoid ? (
                voidReceptionTarget === rec.id ? (
                  <View style={styles.voidPromptRow}>
                    <TextInput
                      style={styles.linkedVoidReasonInput}
                      value={voidReceptionReason}
                      onChangeText={setVoidReceptionReason}
                      placeholder="Motivo"
                      placeholderTextColor={ticketColors.ink3}
                    />
                    <TouchableOpacity
                      style={styles.voidConfirmBtn}
                      accessibilityRole="button"
                      testID="purchases.reception.void.confirm"
                      onPress={() => {
                        if (voidReceptionReason.trim().length < 3) {
                          setActionError('Ingresá un motivo de al menos 3 caracteres.');
                          return;
                        }
                        setActionError(null);
                        onVoidReception(rec.id, voidReceptionReason.trim());
                        setVoidReceptionTarget(null);
                        setVoidReceptionReason('');
                      }}
                    >
                      <Text style={styles.voidConfirmBtnText}>Confirmar</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.voidCancelBtn}
                      onPress={() => {
                        setVoidReceptionTarget(null);
                        setVoidReceptionReason('');
                      }}
                    >
                      <Text style={styles.voidCancelBtnText}>Cancelar</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity
                    accessibilityRole="button"
                    testID={`purchases.reception.void.${rec.id}`}
                    onPress={() => {
                      setVoidReceptionTarget(rec.id);
                      setVoidReceptionReason('');
                      setActionError(null);
                    }}
                    disabled={submitting}
                  >
                    <Text style={styles.seeAll}>Anular</Text>
                  </TouchableOpacity>
                )
              ) : null}
            </View>
          ))}
        </View>
      ) : null}

      {/* BUG-45 + proto: devoluciones ligadas con anulación en 2 pasos */}
      {returns.length > 0 ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>Devoluciones ({returns.length})</Text>
          {returns.map((ret, index) => (
            <View
              key={ret.id}
              style={[styles.miniRow, index === returns.length - 1 && styles.miniRowLast]}
            >
              <View style={styles.miniMain}>
                <Text style={styles.miniTitle}>Devolución {shortDay(ret.returnedAt)}</Text>
                <Text style={styles.miniMeta} numberOfLines={1}>
                  {ret.reason ?? 'sin motivo'}
                </Text>
              </View>
              <StatusPill
                label={ret.status === 'active' ? 'Activa' : 'Anulada'}
                tone={ret.status === 'active' ? 'success' : 'neutral'}
                compact
              />
              {ret.status === 'active' &&
              fiscalCaptureEnabled &&
              onRegisterSupplierCreditNote &&
              order ? (
                <TouchableOpacity
                  onPress={() =>
                    onRegisterSupplierCreditNote({
                      returnId: ret.id,
                      receptionId: ret.receptionId,
                      order,
                    })
                  }
                  accessibilityRole="button"
                  testID={`purchases.return.register-nc.${ret.id}`}
                >
                  <Text style={styles.seeAll}>Registrar NC ›</Text>
                </TouchableOpacity>
              ) : null}
              {ret.status === 'active' && canVoid ? (
                voidReturnTarget === ret.id ? (
                  <View style={styles.voidPromptRow}>
                    <TextInput
                      style={styles.linkedVoidReasonInput}
                      value={voidReturnReason}
                      onChangeText={setVoidReturnReason}
                      placeholder="Motivo"
                      placeholderTextColor={ticketColors.ink3}
                    />
                    <TouchableOpacity
                      style={styles.voidConfirmBtn}
                      onPress={() => {
                        if (voidReturnReason.trim().length < 3) {
                          setActionError('Ingresá un motivo de al menos 3 caracteres.');
                          return;
                        }
                        setActionError(null);
                        onVoidReturn(ret.id, voidReturnReason.trim());
                        setVoidReturnTarget(null);
                        setVoidReturnReason('');
                      }}
                    >
                      <Text style={styles.voidConfirmBtnText}>Confirmar</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.voidCancelBtn}
                      onPress={() => {
                        setVoidReturnTarget(null);
                        setVoidReturnReason('');
                      }}
                    >
                      <Text style={styles.voidCancelBtnText}>Cancelar</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity
                    accessibilityRole="button"
                    testID={`purchases.return.void.${ret.id}`}
                    onPress={() => {
                      setVoidReturnTarget(ret.id);
                      setVoidReturnReason('');
                      setActionError(null);
                    }}
                    disabled={submitting}
                  >
                    <Text style={styles.seeAll}>Anular</Text>
                  </TouchableOpacity>
                )
              ) : null}
            </View>
          ))}
        </View>
      ) : null}

      {/* Proto v-detail: "Pago del gasto" — todos los gastos ligados con pill + pagar */}
      {allLinkedExpenses.length > 0 ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>
            {allLinkedExpenses.length === 1
              ? 'Pago del gasto'
              : `Gastos ligados (${allLinkedExpenses.length})`}
          </Text>
          {allLinkedExpenses.map((expense, index) => {
            const pending = Math.max(0, expense.amount - expense.amountPaid);
            const isOverdue =
              expense.dueDate != null && expense.dueDate < todayIso && pending > 0;
            const canPayThis =
              canPay &&
              (expense.paymentStatus === 'unpaid' || expense.paymentStatus === 'partial');
            const payPill =
              expense.paymentStatus === 'paid'
                ? { label: 'Pagado', tone: 'success' as const }
                : isOverdue
                  ? { label: 'Vencido', tone: 'danger' as const }
                  : expense.paymentStatus === 'partial'
                    ? { label: 'Parcial', tone: 'warning' as const }
                    : { label: 'Pendiente', tone: 'warning' as const };
            return (
              <View
                key={expense.id}
                style={[
                  styles.miniRow,
                  index === allLinkedExpenses.length - 1 && styles.miniRowLast,
                ]}
              >
                <View style={styles.miniMain}>
                  <Text style={styles.miniTitle} numberOfLines={1}>
                    Gasto · {order.orderNumber ?? 'OC'} · {order.supplierName ?? 'Sin proveedor'}
                  </Text>
                  <Text style={styles.miniMeta} numberOfLines={1}>
                    {formatMoney(expense.amount)}
                    {expense.dueDate ? ` · vence ${shortDay(expense.dueDate)}` : ''}
                    {' · '}
                    {pending > 0 ? `restan ${formatMoney(pending)}` : 'pagado'}
                  </Text>
                </View>
                <StatusPill label={payPill.label} tone={payPill.tone} compact />
                {fiscalCaptureEnabled && expense.invoiceDocType == null ? (
                  <StatusPill label="Falta factura fiscal" tone="warning" compact />
                ) : null}
                {fiscalCaptureEnabled &&
                expense.invoiceDocType == null &&
                onCompleteExpenseFiscal &&
                canEdit ? (
                  <TouchableOpacity
                    onPress={() => onCompleteExpenseFiscal(expense)}
                    disabled={submitting}
                    accessibilityRole="button"
                    testID={`purchases.expense.complete-fiscal.${expense.id}`}
                  >
                    <Text style={styles.seeAll}>Completar factura ›</Text>
                  </TouchableOpacity>
                ) : null}
                {canPayThis ? (
                  <TouchableOpacity
                    onPress={() => onPay(expense)}
                    disabled={submitting}
                    accessibilityRole="button"
                    accessibilityLabel={`Pagar ${formatMoney(pending)}`}
                    testID={`purchases.expense.pay.${expense.id}`}
                  >
                    <Text style={styles.seeAll}>Pagar ›</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            );
          })}
          {allLinkedExpenses.length > 1 ? (
            <Text style={styles.hint}>
              Si recibiste en dos tandas, cada recepción puede tener su propio gasto — acá se
              ven todos.
            </Text>
          ) : null}
        </View>
      ) : null}

      {order.notes ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>Nota</Text>
          <Text style={styles.sectionText}>{order.notes}</Text>
        </View>
      ) : null}

      {/* Proto v-detail: info-box de comprobante, o sección si ya hay adjuntos */}
      {linkedExpense ? (
        attachments.length === 0 ? (
          <TouchableOpacity
            style={styles.attachInfoBox}
            onPress={() => void handleAttachReceipt()}
            disabled={attachmentBusy || submitting}
            accessibilityRole="button"
            accessibilityLabel={expenseErpCopy.attachmentAddLabel}
          >
            <Text style={styles.attachInfoText}>
              {attachmentBusy ? 'Subiendo…' : 'Sin comprobante'}
            </Text>
            {!attachmentBusy ? (
              <Text style={styles.seeAll}>Adjuntar factura ›</Text>
            ) : null}
          </TouchableOpacity>
        ) : (
          <View style={styles.secDivided}>
            <Text style={styles.secTitle}>{expenseErpCopy.attachmentLabel}</Text>
            {attachments.map((att, index) => (
              <View
                key={att.id}
                style={[
                  styles.miniRow,
                  index === attachments.length - 1 && styles.miniRowLast,
                ]}
              >
                <View style={styles.miniMain}>
                  <Text style={styles.miniTitle} numberOfLines={1}>
                    {att.fileName}
                  </Text>
                </View>
              </View>
            ))}
            <TouchableOpacity
              onPress={() => void handleAttachReceipt()}
              disabled={attachmentBusy || submitting}
              accessibilityRole="button"
              accessibilityLabel={expenseErpCopy.attachmentAddLabel}
            >
              <Text style={styles.seeAll}>
                {attachmentBusy ? 'Subiendo…' : `${expenseErpCopy.attachmentAddLabel} ›`}
              </Text>
            </TouchableOpacity>
          </View>
        )
      ) : null}

      {/* Proto v-detail: act-grid con las acciones de la orden (mismos gates) */}
      {voidPrompt || closePrompt ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>
            {voidPrompt ? 'Anular orden' : 'Cerrar faltante'}
          </Text>
          {actionError ? <Text style={styles.actionError}>{actionError}</Text> : null}
          <TextInput
            style={styles.voidReasonInput}
            value={voidPrompt ? voidReason : closeReason}
            onChangeText={voidPrompt ? setVoidReason : setCloseReason}
            placeholder={voidPrompt ? 'Motivo de anulación' : 'Motivo de cierre'}
            placeholderTextColor={ticketColors.ink3}
            testID={voidPrompt ? 'purchases.void.reason' : undefined}
          />
          <View style={styles.promptBtnsRow}>
            <TouchableOpacity
              style={styles.promptCancelBtn}
              onPress={() => {
                setVoidPrompt(false);
                setVoidReason('');
                setClosePrompt(false);
                setCloseReason('');
                setActionError(null);
              }}
              accessibilityRole="button"
            >
              <Text style={styles.promptCancelText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.promptConfirmBtn,
                voidPrompt && styles.promptConfirmDanger,
                submitting && styles.saveBtnDisabled,
              ]}
              onPress={voidPrompt ? handleVoidPress : handleCloseLeftoverPress}
              disabled={submitting}
              accessibilityRole="button"
              accessibilityLabel={voidPrompt ? 'Confirmar anulación' : 'Confirmar cierre'}
              testID={voidPrompt ? 'purchases.void.confirm' : undefined}
            >
              <Text style={styles.promptConfirmText}>
                {voidPrompt ? 'Confirmar anulación' : 'Confirmar cierre'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : canReceiveOrder ||
        canSendOrder ||
        canPayOrder ||
        canReturnOrder ||
        canCloseOrder ||
        canShareOrder ||
        canVoidOrder ||
        canDuplicate ? (
        <View style={styles.secDivided}>
          <Text style={styles.secTitle}>Acciones</Text>
          {actionError ? <Text style={styles.actionError}>{actionError}</Text> : null}
          <View style={modalExperience.detailActionsGrid}>
            {canReceiveOrder ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  modalExperience.detailActionBtnPrimary,
                  styles.actionBtnFull,
                ]}
                onPress={() => setReceptionMode(true)}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Recibir mercadería"
              >
                <Ionicons name="download-outline" size={17} color="#FFFFFF" />
                <Text style={modalExperience.detailActionTextPrimary}>
                  Recibir mercadería
                </Text>
              </TouchableOpacity>
            ) : canSendOrder ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  modalExperience.detailActionBtnPrimary,
                  styles.actionBtnFull,
                ]}
                onPress={() => onSend(order.id)}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Marcar como enviada"
              >
                <Ionicons name="send-outline" size={17} color="#FFFFFF" />
                <Text style={modalExperience.detailActionTextPrimary}>
                  Marcar enviada
                </Text>
              </TouchableOpacity>
            ) : null}
            {canPayOrder && linkedExpense ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  !isMobile && styles.actionBtnThird,
                ]}
                onPress={() => onPay(linkedExpense)}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Pagar gasto"
              >
                <Ionicons name="cash-outline" size={17} color={ticketColors.ink} />
                <Text style={modalExperience.detailActionText}>Pagar gasto</Text>
              </TouchableOpacity>
            ) : null}
            {canReturnOrder ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  !isMobile && styles.actionBtnThird,
                ]}
                onPress={() => onReturn(order)}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Devolver"
              >
                <Ionicons name="arrow-undo-outline" size={17} color={ticketColors.ink} />
                <Text style={modalExperience.detailActionText}>Devolver</Text>
              </TouchableOpacity>
            ) : null}
            {canCloseOrder ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  !isMobile && styles.actionBtnThird,
                ]}
                onPress={handleCloseLeftoverPress}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Cerrar faltante"
              >
                <Ionicons name="cut-outline" size={17} color={ticketColors.ink} />
                <Text style={modalExperience.detailActionText}>Cerrar faltante</Text>
              </TouchableOpacity>
            ) : null}
            {canShareOrder ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  !isMobile && styles.actionBtnThird,
                ]}
                onPress={() => void handleShare()}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Compartir"
              >
                <Ionicons name="share-social-outline" size={17} color={ticketColors.ink} />
                <Text style={modalExperience.detailActionText}>Compartir</Text>
              </TouchableOpacity>
            ) : null}
            {canDuplicate ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  !isMobile && styles.actionBtnThird,
                ]}
                onPress={() => onDuplicate?.(order)}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Duplicar como borrador"
                testID="purchases.duplicate-draft"
              >
                <Ionicons name="copy-outline" size={17} color={ticketColors.ink} />
                <Text style={modalExperience.detailActionText}>Duplicar</Text>
              </TouchableOpacity>
            ) : null}
            {canVoidOrder ? (
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  styles.actionBtnDanger,
                  !isMobile && styles.actionBtnThird,
                ]}
                onPress={handleVoidPress}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Anular orden"
                testID="purchases.void"
              >
                <Ionicons name="trash-outline" size={17} color={ticketColors.bad} />
                <Text style={styles.actionBtnDangerText}>Anular orden</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      ) : null}
    </View>
  );

  const actions = receptionMode ? (
    <View style={[styles.modalActions, modalExperience.footerActions]}>
      {actionError ? <Text style={styles.actionError}>{actionError}</Text> : null}
      <TouchableOpacity
        style={styles.cancelBtnDesk}
        onPress={() => setReceptionMode(false)}
      >
        <Text style={styles.cancelBtnDeskText}>Cancelar</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.saveBtnDesk, submitting && styles.saveBtnDisabled]}
        onPress={() => void handleConfirmReception()}
        disabled={submitting}
        accessibilityRole="button"
        accessibilityLabel={confirmReceptionLabel}
      >
        <Text style={styles.saveBtnDeskText}>{confirmReceptionLabel}</Text>
      </TouchableOpacity>
    </View>
  ) : (
    // Proto v-detail: el footer solo lleva "Cerrar" — las acciones viven en el act-grid del body.
    <View style={styles.detailFoot}>
      {actionError && !voidPrompt && !closePrompt ? (
        <Text style={styles.actionError}>{actionError}</Text>
      ) : null}
      <TouchableOpacity
        style={[styles.detailFootBtn, styles.detailFootGhost]}
        onPress={onClose}
        accessibilityRole="button"
      >
        <Text style={styles.detailFootGhostText}>Cerrar</Text>
      </TouchableOpacity>
    </View>
  );

  if (isMobile) {
    return (
      <>
      <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
        <View style={styles.sheetBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
          <KeyboardAvoidingView behavior="padding" style={styles.sheetKeyboardWrap}>
            <View style={[styles.sheetCard, { maxHeight: winH * 0.9 }]}>
              <View style={styles.grabber} />
              <View style={styles.sheetHeader}>
                <View style={styles.sheetHeaderMain}>
                  <Text style={styles.sheetKicker}>
                    {receptionMode
                      ? `Compras · ${order.orderNumber ?? 'OC'}`
                      : `Compras · ${order.supplierName ?? 'Sin proveedor'}`}
                  </Text>
                  <View style={styles.sheetTitleRow}>
                    <Text style={styles.sheetTitle} numberOfLines={1}>
                      {receptionMode ? 'Registrar recepción' : orderTitle}
                    </Text>
                    {!receptionMode ? (
                      <StatusPill
                        label={purchaseOrderStatusLabel(order.status)}
                        tone={statusTone(order.status)}
                        compact
                      />
                    ) : null}
                  </View>
                </View>
                <TouchableOpacity onPress={onClose} hitSlop={8}>
                  <Text style={styles.closeBtn}>×</Text>
                </TouchableOpacity>
              </View>
              <ModalAwareScrollView
                contentContainerStyle={receptionMode ? undefined : styles.sheetBodyPad}
              >
                {content}
              </ModalAwareScrollView>
              <View style={styles.sheetFooter}>
                {actions}
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
      {ReceptionConfirmModal}
      </>
    );
  }

  return (
    <>
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View style={[styles.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            styles.modalCard,
            styles.modalCardWidth,
          ]}
        >
          <ModalScreenHeader
            kicker={
              receptionMode
                ? `Compras · ${order.orderNumber ?? 'OC'}`
                : `Compras · ${order.supplierName ?? 'Sin proveedor'}`
            }
            title={receptionMode ? 'Registrar recepción' : orderTitle}
            subtitle={
              receptionMode
                ? 'Indicá cuántas unidades recibiste de cada item.'
                : headerSub || null
            }
            trailing={
              !receptionMode ? (
                <StatusPill
                  label={purchaseOrderStatusLabel(order.status)}
                  tone={statusTone(order.status)}
                  compact
                />
              ) : undefined
            }
            onClose={onClose}
          />
          <ModalFormScroll
            contentContainerStyle={receptionMode ? styles.receptionScrollPad : styles.deskBodyPad}
          >
            {content}
          </ModalFormScroll>
          {actions}
        </View>
      </View>
    </Modal>
    {ReceptionConfirmModal}
    </>
  );
}

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  sheetKeyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
  },
  grabber: {
    width: 40,
    height: 4,
    backgroundColor: ticketColors.ruleSoft,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 8,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  sheetHeaderMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  sheetKicker: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  sheetTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sheetTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  closeBtn: {
    fontSize: 24,
    color: ticketColors.ink3,
  },
  sheetFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  modalBackdrop: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalCard: {
    maxHeight: '96%',
    gap: 8,
  },
  /** Proto v-detail: modal wide — 680px. */
  modalCardWidth: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  /** Body desk: aire como el proto (.modal-body padding 16/20). */
  deskBodyPad: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  /** Recepción: las filas ya llevan padding/border propios — solo aire vertical. */
  receptionScrollPad: {
    paddingVertical: 4,
  },
  /** Sheet mobile: mismo aire que tenía detailBody antes del shell compartido. */
  sheetBodyPad: {
    padding: 16,
  },
  modalActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  cancelBtnDesk: {
    padding: 10,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    flexGrow: 1,
    flexBasis: 140,
  },
  cancelBtnDeskText: {
    color: ticketColors.ink2,
  },
  saveBtnDesk: {
    padding: 10,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: ticketColors.brandPrimary,
    flexGrow: 1,
    flexBasis: 140,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  saveBtnDeskText: {
    color: '#fff',
    fontWeight: '600',
  },
  creditBanner: {
    paddingVertical: 9,
    paddingHorizontal: 11,
    borderRadius: 10,
    backgroundColor: ticketColors.warnBg,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
  },
  creditBannerText: {
    fontSize: 14,
    color: ticketColors.warn,
    fontWeight: '600',
  },
  voidInfoBox: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(220, 38, 38, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(220, 38, 38, 0.24)',
    gap: 2,
  },
  closeInfoBox: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: 2,
  },
  voidInfoTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  voidInfoText: {
    fontSize: 13,
    color: ticketColors.ink2,
  },
  /* ── Proto compras v-detail ─────────────────────────────────────── */
  detailBody: {
    gap: 14,
  },
  statRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    overflow: 'hidden',
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 6,
    gap: 1,
    minWidth: 0,
  },
  statDivider: {
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: ticketColors.ruleSoft,
  },
  statValue: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.sans,
  },
  statValueWarn: {
    color: ticketColors.warn,
  },
  statValueBad: {
    color: ticketColors.bad,
  },
  statValueOk: {
    color: ticketColors.ok,
  },
  statValueMuted: {
    color: ticketColors.ink3,
  },
  statLabel: {
    fontSize: 10.5,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  progressBlock: {
    gap: 6,
  },
  progressLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  progressPct: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontVariant: ['tabular-nums'],
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#EAF3F0',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: ticketColors.brandPrimary,
  },
  warnBox: {
    borderRadius: 10,
    backgroundColor: ticketColors.warnBg,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    paddingVertical: 9,
    paddingHorizontal: 11,
  },
  warnBoxText: {
    fontSize: 12,
    color: ticketColors.warn,
    lineHeight: 17,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  secDivided: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    paddingTop: 14,
  },
  secTitle: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    marginBottom: 8,
    fontFamily: ticketFonts.sans,
  },
  miniRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 9,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  miniRowLast: {
    borderBottomWidth: 0,
  },
  miniMain: {
    flex: 1,
    minWidth: 0,
  },
  miniTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  miniMeta: {
    fontSize: 11.5,
    color: ticketColors.ink3,
    marginTop: 1,
    fontFamily: ticketFonts.sans,
  },
  miniValue: {
    fontSize: 13,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.sans,
  },
  seeAll: {
    fontSize: 12,
    color: ticketColors.brandPrimary,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  hint: {
    fontSize: 11.5,
    color: ticketColors.ink3,
    marginTop: 6,
    lineHeight: 16,
    fontFamily: ticketFonts.sans,
  },
  attachInfoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  attachInfoText: {
    fontSize: 12.5,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  actionBtnFull: {
    flexBasis: '100%',
  },
  actionBtnThird: {
    flexBasis: '30%',
  },
  actionBtnDanger: {
    borderColor: 'rgba(220, 38, 38, 0.3)',
  },
  actionBtnDangerText: {
    fontSize: 14,
    color: ticketColors.bad,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  promptBtnsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  promptCancelBtn: {
    flex: 1,
    minHeight: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promptCancelText: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  promptConfirmBtn: {
    flex: 1,
    minHeight: 40,
    borderRadius: 10,
    backgroundColor: ticketColors.brandPrimary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promptConfirmDanger: {
    backgroundColor: ticketColors.bad,
  },
  promptConfirmText: {
    fontSize: 13,
    color: '#fff',
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  detailFoot: {
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  detailFootBtn: {
    flex: 1,
    minHeight: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailFootGhost: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  detailFootGhostText: {
    fontSize: 14,
    color: ticketColors.ink,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  voidPromptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  linkedVoidReasonInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontSize: 13,
    color: ticketColors.ink,
  },
  voidConfirmBtn: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: ticketColors.bad,
    borderRadius: 6,
  },
  voidConfirmBtnText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
  },
  voidCancelBtn: {
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  voidCancelBtnText: {
    fontSize: 12,
    color: ticketColors.ink3,
  },
  sectionText: {
    fontSize: 13,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  receptionItemRow: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  receptionItemName: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  receptionItemPending: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 2,
  },
  receptionItemInputs: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  // #694: distribución por variante (colores existentes del módulo)
  variantSection: {
    marginTop: 8,
  },
  variantToggleBtn: {
    alignSelf: 'flex-start',
  },
  variantToggleText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  variantSectionBody: {
    marginTop: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: 10,
    gap: 6,
  },
  variantEvenBtn: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.brandSoft,
  },
  variantEvenBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.brandInk,
  },
  variantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  variantLabel: {
    flex: 1,
    fontSize: 13,
    color: ticketColors.ink,
  },
  variantInputWrap: {
    width: 110,
  },
  variantSumText: {
    fontSize: 12,
    fontWeight: '600',
  },
  variantSumOk: {
    color: ticketColors.ink2,
  },
  variantSumMismatch: {
    color: ticketColors.bad,
  },
  flex1: {
    flex: 1,
  },
  inputLabel: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginBottom: 4,
  },
  expenseToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: ticketColors.rule,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxActive: {
    backgroundColor: ticketColors.brandPrimary,
    borderColor: ticketColors.brandPrimary,
  },
  checkboxTick: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  expenseToggleLabel: {
    fontSize: 14,
    color: ticketColors.ink,
  },
  fiscalBlock: {
    marginTop: 8,
    gap: 8,
  },
  fiscalToggle: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  fiscalHint: {
    fontSize: 12.5,
    color: ticketColors.ink3,
    lineHeight: 18,
  },
  actionError: {
    width: '100%',
    color: ticketColors.bad,
    fontSize: 13,
    fontWeight: '600',
  },
  voidReasonInput: {
    flexGrow: 1,
    minWidth: 160,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: ticketColors.ink,
  },
});
